/**
 * 图片属性面板（与 Vue ImageProperties.vue 对应）
 */
import { Input, Label, ListBox, Select } from '@heroui/react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { ElementPatch, ImageElement } from '@/components/canvas/document/publicTypes';
import { boxSizeControlPatchToElementPatch, layoutControlPatchToElementPatch } from '@/components/canvas/elements/model';
import {
  AppearanceSettings,
  BoxSizeSettings,
  LayoutSettings,
  appearanceValueFromAppearance,
  appearanceValueToAppearancePatch,
  boxSizeSettingsValueFromElement,
  layoutSettingsValueFromElement,
  type AppearanceSettingsValue,
  type BoxSizeSettingsValue,
  type CanvasAlignAction,
  type LayoutSettingsValue,
} from '@/components/canvas/shared/controls';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export interface ImagePropertiesProps {
  element: ImageElement;
}

export function ImageProperties({ element: el }: ImagePropertiesProps) {
  const { updateElement, getCurrentPage } = useCanvas();
  const patch = (partial: ElementPatch<'image'>) => updateElement(el.id, partial);
  const appearanceValue = appearanceValueFromAppearance(el.appearance);

  const handleLayoutChange = (partial: Partial<LayoutSettingsValue>) => {
    patch(layoutControlPatchToElementPatch(el, partial) as ElementPatch<'image'>);
  };

  const handleBoxSizeChange = (partial: Partial<BoxSizeSettingsValue>) => {
    patch(boxSizeControlPatchToElementPatch(partial) as ElementPatch<'image'>);
  };

  const handleAppearanceChange = (partial: Partial<AppearanceSettingsValue>) => {
    const next = { ...appearanceValue, ...partial };
    patch({
      appearance: { ...el.appearance, ...appearanceValueToAppearancePatch(next, el.appearance) },
    });
  };

  const handleAlignToCanvas = (action: CanvasAlignAction) => {
    const page = getCurrentPage();
    if (!page) return;
    const { width: cw, height: ch } = page.size;
    let x = el.layout.x;
    let y = el.layout.y;
    if (action === 'left') x = 0;
    if (action === 'right') x = cw - el.size.width;
    if (action === 'top') y = 0;
    if (action === 'bottom') y = ch - el.size.height;
    if (action === 'center') {
      x = (cw - el.size.width) / 2;
      y = (ch - el.size.height) / 2;
    }
    patch({ layout: { x, y } });
  };

  const objectFit = el.data.objectFit ?? 'cover';

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-4 p-1">
      <BoxSizeSettings value={boxSizeSettingsValueFromElement(el)} onChange={handleBoxSizeChange} />
      <LayoutSettings
        value={layoutSettingsValueFromElement(el)}
        onChange={handleLayoutChange}
        onAlignToCanvas={handleAlignToCanvas}
      />
      <AppearanceSettings value={appearanceValue} onChange={handleAppearanceChange} />

      <section className="w-full shrink-0 space-y-2 p-1">
        <h3 className="text-sm font-semibold text-default-foreground">图片</h3>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">适应方式</Label>
            <Select
              aria-label="适应方式"
              value={objectFit}
              onChange={(key) => {
                if (
                  key === 'fill' ||
                  key === 'contain' ||
                  key === 'cover' ||
                  key === 'none' ||
                  key === 'scale-down'
                ) {
                  patch({ data: { objectFit: key as ImageElement['data']['objectFit'] } });
                }
              }}
            >
              <Select.Trigger className="h-8">
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  <ListBox.Item id="fill" textValue="填充">
                    填充
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="contain" textValue="包含">
                    包含
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="cover" textValue="覆盖">
                    覆盖
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="none" textValue="无">
                    无
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="scale-down" textValue="缩小">
                    缩小
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </Select.Popover>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">缩放</Label>
              <Input
                aria-label="图片缩放"
                type="number"
                min={0.1}
                step={0.1}
                value={el.data.imageScale ?? 1}
                onChange={(e) => patch({ data: { imageScale: Math.max(0.1, num(e.target.value)) } })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">水平锚点</Label>
              <Input
                aria-label="图片水平锚点（百分比）"
                type="number"
                min={0}
                max={100}
                value={el.data.objectPosition?.x ?? 50}
                onChange={(e) =>
                  patch({
                    data: {
                      objectPosition: {
                        x: Math.max(0, Math.min(100, num(e.target.value))),
                        y: el.data.objectPosition?.y ?? 50,
                      },
                    },
                  })
                }
                className="h-8"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">垂直锚点</Label>
            <Input
              aria-label="图片垂直锚点（百分比）"
              type="number"
              min={0}
              max={100}
              value={el.data.objectPosition?.y ?? 50}
              onChange={(e) =>
                patch({
                  data: {
                    objectPosition: {
                      x: el.data.objectPosition?.x ?? 50,
                      y: Math.max(0, Math.min(100, num(e.target.value))),
                    },
                  },
                })
              }
              className="h-8"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
